import { formatDistance, UNIT_METRIC, UnitSystem } from '@strava/units';

import {
  FORMAT_DISTANCE_BASE_OPTIONS,
  KILOMETERS_TO_METERS,
  METERS_TO_FEET,
  MILES_TO_METERS,
  SCALE_MAX_WIDTH
} from './constants';

/**
 * Utility function to round a given decimal number to the nearest "natural" precision
 * i.e., to an appropriate number of decimal places depending on how small the input number is
 * Note: This is based on a calculation used by `maplibre-gl-js` (https://github.com/maplibre/maplibre-gl-js/blob/be7e20920554d5ed873448c96e5fa73e7e2ce565/src/ui/control/scale_control.ts#L136)
 */
export const getDecimalRoundNumber = (decimal: number) => {
  const multiplier = Math.pow(10, Math.ceil(-Math.log(decimal) / Math.LN10)); // Calculate the multiplier
  return Math.round(decimal * multiplier) / multiplier; // Round the decimal
};

/**
 * Unitity function to round a number to a "rounder" value depending on its magnitude.
 * It aims to round the input number to a significant figure that is either 1, 2, 3, 5, or 10 (or to a smaller decimal value if necessary).
 * Note: This is based on a calculation used by `maplibre-gl-js` (https://github.com/maplibre/maplibre-gl-js/blob/be7e20920554d5ed873448c96e5fa73e7e2ce565/src/ui/control/scale_control.ts#L141)
 *
 * Examples:
 * getNumberRoundedToSignificantFigure(753) → 500
 * getNumberRoundedToSignificantFigure(1753) → 2000
 * getNumberRoundedToSignificantFigure(0.0123) → Calls getDecimalRoundNumber to round the decimal
 */
export const getNumberRoundedToSignificantFigure = (number: number) => {
  const pow10 = Math.pow(10, `${Math.floor(number)}`.length - 1); // Calculate the power of 10
  let d = number / pow10; // Scale down the number

  // Round d to a significant figure:
  d =
    d >= 10
      ? 10
      : d >= 5
        ? 5
        : d >= 3
          ? 3
          : d >= 2
            ? 2
            : d >= 1
              ? 1
              : getDecimalRoundNumber(d);

  return pow10 * d; // Scale the rounded value back
};

/**
 * Utility function which calculates a scaled width based on a ratio between two distances
 * and a maximum width, i.e. SCALE_MAX_WIDTH
 *
 */
export const getScaleWidth = (
  distance: number,
  roundedDistance: number
): number => (roundedDistance / distance) * SCALE_MAX_WIDTH;

/**
 * Function takes `locale`, `unitSystem` and `meters` distance as inputs
 * If metric and less than 1 km => returns localized scale label in meters and scale width
 * If metric and greater than or equal to 1 km => returns localized scale label in kilometers and scale width
 * If imperial and less than 1 mile => returns localized scale label in feet and scale width
 * If imperial and greater than or equal to 1 mile => returns localized scale label in miles and scale width
 */
export const getScale = (
  locale: string,
  unitSystem: UnitSystem,
  meters: number
): { scaleLabel: string; scaleWidth: number } => {
  let distance: number;
  let roundedDistance: number;
  let scaleLabel: string;

  // if metric
  if (unitSystem === UNIT_METRIC) {
    // if less than 1km => use meters
    if (meters < KILOMETERS_TO_METERS) {
      distance = meters * 1000;
      roundedDistance = getNumberRoundedToSignificantFigure(distance);
      scaleLabel = formatDistance(locale, unitSystem, roundedDistance, {
        unit: 'meter',
        ...FORMAT_DISTANCE_BASE_OPTIONS
      });
    } else {
      // if 1km or more => use kilometers
      distance = meters;
      roundedDistance = getNumberRoundedToSignificantFigure(distance);
      scaleLabel = formatDistance(
        locale,
        unitSystem,
        roundedDistance,
        FORMAT_DISTANCE_BASE_OPTIONS
      );
    }
  } else {
    // if imperial and less than 1 mile => use feet
    if (meters < MILES_TO_METERS) {
      distance = meters * METERS_TO_FEET;
      roundedDistance = getNumberRoundedToSignificantFigure(distance);
      // NOTE: Using `Intl.NumberFormat` here because `formatDistance` does not support `ft`
      scaleLabel = new Intl.NumberFormat(locale, {
        ...FORMAT_DISTANCE_BASE_OPTIONS,
        unit: 'foot'
      }).format(roundedDistance);
    } else {
      // if imperial and 1 mile or more => use miles
      const miles = meters / MILES_TO_METERS; // get miles
      const roundMiles = getNumberRoundedToSignificantFigure(miles); // round miles

      distance = meters;
      roundedDistance = roundMiles * MILES_TO_METERS; // get round miles as meters
      scaleLabel = formatDistance(
        locale,
        unitSystem,
        roundedDistance,
        FORMAT_DISTANCE_BASE_OPTIONS
      );
    }
  }

  return { scaleLabel, scaleWidth: getScaleWidth(distance, roundedDistance) };
};
